// BUILD-046 correction cycle 1bb88b3e (stage B2b, items N2 and N3): the focus behavior of a modal dialog, as one hook.
//
//   - the control that had focus when the dialog opened is remembered BEFORE the dialog draws (so the dialog's own autofocus cannot replace it), and
//     gets focus back when the dialog closes or goes away (if it is still on the page);
//   - Tab and Shift+Tab stay inside the dialog (the rule is dialogFocusModel.trapTab), also when focus has fallen onto the page body (a button that
//     was focused became disabled while a request ran);
//   - Escape calls `onEscape` (the dialog's own close) even when focus is on the page body; a handler lower down that already handled Escape (the open
//     people list closes first) marks the event handled and the dialog stays;
//   - `focusFirst` puts focus on the first control that wants it (an element with data-autofocus, else the first focusable) when the dialog appears.
//
// The key handling is one document listener that exists only while the dialog is active, so it works wherever focus is.
import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import { trapTab } from './dialogFocusModel';

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

function focusablesIn(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.tabIndex >= 0 && (el.offsetParent !== null || el === document.activeElement));
}

export interface DialogFocus {
  /** Focus the dialog's first wanted control (call when the dialog content has appeared). */
  focusFirst: () => void;
  /** Focus a control by id inside the dialog (for example the submit button after a failure took focus away). */
  focusId: (id: string) => void;
}

/**
 * `active` is true while the dialog is shown. `containerRef` is the dialog element. The opener is read during the render in which `active` turns true,
 * which is before the dialog's own autofocus runs. `onEscape` is the dialog's close.
 */
export function useDialogFocus(active: boolean, containerRef: RefObject<HTMLElement>, onEscape?: () => void): DialogFocus {
  const wasActive = useRef(false);
  const opener = useRef<HTMLElement | null>(null);
  if (active && !wasActive.current) opener.current = typeof document !== 'undefined' && document.activeElement instanceof HTMLElement ? document.activeElement : null;
  if (!active) wasActive.current = false;
  else wasActive.current = true;

  const escapeRef = useRef(onEscape);
  escapeRef.current = onEscape;

  // Focus goes back to the opener when the dialog closes (or is removed). The opener is kept until the next opening replaces it.
  useEffect(() => {
    if (!active) return undefined;
    return () => {
      const target = opener.current;
      if (target !== null && document.contains(target)) target.focus();
    };
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        if (escapeRef.current) {
          event.preventDefault();
          escapeRef.current();
        }
        return;
      }
      if (event.key !== 'Tab') return;
      const container = containerRef.current;
      if (container === null) return;
      const items = focusablesIn(container);
      const at = items.findIndex((el) => el === document.activeElement);
      const decision = trapTab(items.length, at, event.shiftKey);
      if (!decision.prevent) return;
      event.preventDefault();
      if (decision.index >= 0) items[decision.index]?.focus();
      else container.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active, containerRef]);

  const focusFirst = useCallback(() => {
    const container = containerRef.current;
    if (container === null) return;
    const wanted = container.querySelector<HTMLElement>('[data-autofocus]');
    (wanted ?? focusablesIn(container)[0] ?? container).focus();
  }, [containerRef]);

  const focusId = useCallback(
    (id: string) => {
      const container = containerRef.current;
      container?.querySelector<HTMLElement>(`#${id}`)?.focus();
    },
    [containerRef]
  );

  return { focusFirst, focusId };
}
